/**
 * @file getcampaign.js
 * @description Get Campaigns component for displaying and managing campaigns.
 * @component Campaigns
 * @exports Campaigns - Main component for displaying and managing campaigns.
 */

import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  Container,
  Alert,
  Box,
  IconButton,
  MenuItem,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import CardComponent from "../common/components/CardComponent";
import DialogComponent from "../common/components/DialogComponent.jsx";
import { getcampaigns, AddCampaign, DeleteCampaign } from "../services/api";
import Schedule from "../features/schedule/Schedule.jsx";
import { getScheduleIds, deleteSchedule } from "../services/schedule.js";
import ProgressOverlay from "../common/components/ProgressOverlay.jsx";

// Filter / field option sets
const CATEGORY_OPTIONS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
  "Other",
];
const LANGUAGE_OPTIONS = ["English", "Spanish"];
const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const todayCategory = WEEKDAYS[new Date().getDay()]; // default Category filter = current day

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(todayCategory);
  const [languageFilter, setLanguageFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedCampaignID, setSelectedCampaignID] = useState(null); // Track selected campaign
  const [isModalOpen, setIsModalOpen] = useState(false); // Track modal open state
  const [confirmDelete, setConfirmDelete] = useState(false); // Track delete confirmation
  const [campaignToDelete, setCampaignToDelete] = useState(null); // Track campaign to delete

  const [newCampaign, setNewCampaign] = useState({
    name: "",
    description: "",
    campaigntypeid: "",
    useai: 0,
    category: todayCategory,
    language: "English",
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  // Load campaigns from API
  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const fetchedData = await getcampaigns();
      if (Array.isArray(fetchedData)) {
        setCampaigns(fetchedData);
      } else {
        console.error("Fetched data is not an array:", fetchedData);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening the "Add Campaign" modal
  const handleAddClick = () => {
    setNewCampaign({
      name: "",
      description: "",
      campaigntypeid: "",
      useai: 0,
      category: todayCategory,
      language: "English",
    });
    setIsModalOpen(true);
  };

  // Handle saving a new campaign
  const handleSave = async () => {
    try {
      setLoading(true);
      await AddCampaign(newCampaign);
      setIsModalOpen(false);
      setNewCampaign({
        name: "",
        description: "",
        campaigntypeid: "",
        useai: 0,
        category: todayCategory,
        language: "English",
      });
      setSuccessMessage("Campaign added successfully!");

      await loadCampaigns(); // Refresh campaigns list

      setTimeout(() => setSuccessMessage(""), 3000); // Clear success message
    } catch (error) {
      console.error("Error adding campaign:", error);
    } finally {
      setLoading(false);
    }
  };

  // Open confirmation dialog before deleting
  const handleDeleteClick = (campaignID) => {
    setCampaignToDelete(campaignID);
    setConfirmDelete(true);
  };

  // Perform soft delete (set inactive = 1)
  const handleConfirmDelete = async () => {
    if (!campaignToDelete) return;
    try {
      setLoading(true);
      setConfirmDelete(false);
      await DeleteCampaign(campaignToDelete);
      var schedules = await getScheduleIds(campaignToDelete);
      if (schedules) {
        await deleteSchedule(schedules);
      }
      setSuccessMessage("Campaign deleted successfully!");

      setCampaignToDelete(null);
      await loadCampaigns(); // Refresh campaign list
    } catch (error) {
      console.error("Error deleting campaign:", error);
    } finally {
      setLoading(false);
    }
  };

  // Apply search + Category + Language filters
  const term = searchTerm.trim().toLowerCase();
  const filteredCampaigns = campaigns.filter((c) => {
    const matchesSearch =
      !term ||
      c.name?.toLowerCase().includes(term) ||
      c.description?.toLowerCase().includes(term);
    const matchesCategory =
      categoryFilter === "All" || c.Category === categoryFilter;
    const matchesLanguage =
      languageFilter === "All" || c.Language === languageFilter;
    return matchesSearch && matchesCategory && matchesLanguage;
  });

  // Show Schedule if a campaign is selected
  if (selectedCampaignID) {
    return (
      <Schedule
        campaignID={selectedCampaignID}
        onBack={() => setSelectedCampaignID(null)}
      />
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {loading && <ProgressOverlay />}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size="grow">
          <TextField
            fullWidth
            label="Search Campaigns"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
          />
        </Grid>
        <Grid size="auto">
          <TextField
            select
            label="Category"
            size="small"
            sx={{ minWidth: 150 }}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {CATEGORY_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size="auto">
          <TextField
            select
            label="Language"
            size="small"
            sx={{ minWidth: 140 }}
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {LANGUAGE_OPTIONS.map((l) => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size="auto">
          <Button variant="contained" color="primary" onClick={handleAddClick}>
            + Add Campaign
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {filteredCampaigns.map((campaign) => (
          <Grid
            size={{ xs: 12, md: 6, lg: 3 }}
            key={campaign.id}
            sx={{
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <CardComponent
              title={campaign.name}
              actions={
                <>
                  <IconButton
                    onClick={() => setSelectedCampaignID(campaign.id)} // Open Schedule
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteClick(campaign.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </>
              }
            >
              <Typography variant="body2">{campaign.description}</Typography>
              {(campaign.Category || campaign.Language) && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  {[campaign.Category, campaign.Language]
                    .filter(Boolean)
                    .join(" \u00B7 ")}
                </Typography>
              )}
            </CardComponent>
          </Grid>
        ))}
      </Grid>
      {/* Add Campaign Modal */}
      <DialogComponent
        title="Add Campaign"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        actions={
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{ mr: 1 }}
          >
            Save
          </Button>
        }
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            label="Campaign Name"
            value={newCampaign.name}
            size="small"
            onChange={(e) =>
              setNewCampaign({ ...newCampaign, name: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Description"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            value={newCampaign.description}
            onChange={(e) =>
              setNewCampaign({ ...newCampaign, description: e.target.value })
            }
          />
          <TextField
            select
            fullWidth
            label="Category"
            size="small"
            value={newCampaign.category}
            onChange={(e) =>
              setNewCampaign({ ...newCampaign, category: e.target.value })
            }
          >
            {CATEGORY_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Language"
            size="small"
            value={newCampaign.language}
            onChange={(e) =>
              setNewCampaign({ ...newCampaign, language: e.target.value })
            }
          >
            {LANGUAGE_OPTIONS.map((l) => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogComponent>

      {/* Delete Confirmation Dialog */}
      <DialogComponent
        title="Confirm Delete"
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        actions={
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        }
      >
        <Typography variant="body">
          Are you sure you want to delete this campaign?
        </Typography>
      </DialogComponent>
    </Container>
  );
};

export default Campaigns;